import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { isPlaceholderGroupName } from "../utils/isPlaceholderGroupName";
import type { RenameAffectedPlansProps } from "./types";

export const RenameAffectedPlans = ({
  oldGroupLabel,
  newGroupLabel,
  plans,
  showMergeWarning,
  listsEveryPlan,
}: RenameAffectedPlansProps) => {
  const { t } = useTranslation();

  const mergingPlans = useMemo(
    () => plans.filter((plan) => plan.willMerge),
    [plans],
  );

  return (
    <>
      <p className="mb-2 text-sm text-gray-600">
        <span className="font-medium text-gray-900">{oldGroupLabel}</span>{" "}
        {t(
          listsEveryPlan
            ? "smartSurveyor.groupInspector.applyRenameAllPlansDescription"
            : "smartSurveyor.groupInspector.applyRenameDescription",
          { count: plans.length },
        )}
      </p>
      {isPlaceholderGroupName(oldGroupLabel) && (
        <p className="mb-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {t("smartSurveyor.groupInspector.applyRenamePlaceholderNote")}
        </p>
      )}
      <ul className="mb-4 max-h-60 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
        {plans.map((plan, index) => (
          <li
            key={plan.floorPlanId}
            className="flex items-start gap-2 px-3 py-2 text-sm text-gray-700"
          >
            <span className="shrink-0 font-medium text-gray-600">
              {index + 1}.
            </span>
            <span className="min-w-0 break-words">
              {plan.floorPlanName}
              {plan.archived && (
                <span className="ml-2 inline-block whitespace-nowrap rounded bg-gray-100 px-1.5 py-0.5 align-middle text-xs font-medium text-gray-600">
                  {t("smartSurveyor.groupInspector.applyRenameArchivedBadge")}
                </span>
              )}
              {showMergeWarning && plan.willMerge && (
                <span className="ml-2 inline-block whitespace-nowrap align-middle text-xs font-medium text-amber-700">
                  {t("smartSurveyor.groupInspector.applyRenameMergeBadge")}
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>
      {showMergeWarning && mergingPlans.length > 0 && (
        <p className="mb-6 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <span className="font-medium">{newGroupLabel}</span>{" "}
          {t("smartSurveyor.groupInspector.applyRenameMergeWarning", {
            count: mergingPlans.length,
          })}
        </p>
      )}
    </>
  );
};
